import * as React from 'react';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { allowTenantEntityScopeChange } from '@/components/access/tenant-entity-switcher';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { workspaceError } from './workspace-utils';

export function AgentEntityPicker({ disabled }: { disabled: boolean }) {
  const access = useTenantEntity();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const alive = React.useRef(true);
  const inFlight = React.useRef(false);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function choose(tenantId: string, entityId: string) {
    if (inFlight.current || disabled || !allowTenantEntityScopeChange()) return;
    inFlight.current = true;
    setBusy(true); setError(undefined);
    try {
      await access.selectContext(tenantId, entityId);
      if (alive.current) setOpen(false);
    } catch (failure) { if (alive.current) setError(workspaceError(failure)); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  }
  const blocked = disabled || busy || access.contextsLoading || access.status === 'switching';
  return <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild><Button type="button" size="sm" variant="outline" disabled={disabled}>Change entity</Button></DialogTrigger>
    <DialogContent className="flex flex-col gap-3">
      <DialogHeader><DialogTitle>Choose Agent entity</DialogTitle><DialogDescription>This changes the app’s selected entity. Existing chats stay in their original entity; only your chats in the new entity are shown.</DialogDescription></DialogHeader>
      <p className="text-sm">Current: {access.selected?.entityName ?? 'No entity selected'}</p>
      {access.contextsLoading && <p role="status">Loading entities…</p>}
      {access.error && <div role="alert"><p>{access.error}</p><Button variant="outline" onClick={() => void access.refresh()}>Retry entities</Button></div>}
      <div className="space-y-2">
        {(access.contexts ?? []).map(context => <Button type="button" className="h-auto w-full justify-start whitespace-normal text-left" variant="outline" key={context.tenantId + ':' + context.entityId}
          disabled={blocked || (context.tenantId === access.selected?.tenantId && context.entityId === access.selected.entityId)}
          onClick={() => void choose(context.tenantId, context.entityId)}>{context.tenantName} · {context.entityName}</Button>)}
        {!access.contextsLoading && !access.contexts?.length && <p>No entities on this page.</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={blocked || !access.contextOffset} onClick={() => access.setContextOffset(Math.max(0, access.contextOffset - 100))}>Previous entities</Button>
        <Button variant="outline" disabled={blocked || access.contextOffset + 100 >= access.contextTotal} onClick={() => access.setContextOffset(access.contextOffset + 100)}>Next entities</Button>
      </div>
      {error && <p role="alert">{error}</p>}
    </DialogContent>
  </Dialog>;
}
